import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { EMPTY, BehaviorSubject, Observable, Subscription, filter, of, take, combineLatest, map } from 'rxjs';

import { Direction, GlobalBookingTripDates, HistogramBar } from '@fcom/common';
import { LocalDate, unsubscribe } from '@fcom/utils';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components';
import { TripType } from '@fcom/core';
import { Amount } from '@fcom/dapi';

import { DatePickerPrices } from '../../interfaces';
import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { getStartingFromPrice } from '../../utils/utils';

@Component({
  selector: 'fin-travel-dates-selector',
  templateUrl: 'travel-dates-selector.component.html',
  styleUrls: ['./travel-dates-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TravelDatesSelectorComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;

  @Input()
  disabled = false;

  @Input()
  travelDates$: Observable<GlobalBookingTripDates>;

  @Input()
  isOneway$: Observable<boolean> = of(false);

  @Input()
  flexibleDates = false;

  @Input()
  showSingular?: keyof GlobalBookingTripDates;

  @Input()
  showSeparator = true;

  @Input()
  prices$: Observable<DatePickerPrices> = EMPTY;

  @Output()
  setTravelDates = new EventEmitter<GlobalBookingTripDates>();

  @Output()
  setFlexibleDates = new EventEmitter<boolean>();

  subscription = new Subscription();
  modalOpen = false;
  calendarRange: [LocalDate, LocalDate] = [LocalDate.now(), LocalDate.now().plusDays(360)];
  pricePerAdult$: Observable<Amount> = of(undefined);

  selectedHistogramMonth$: BehaviorSubject<number> = new BehaviorSubject(undefined);

  @ViewChild('calendarHeader')
  calendarHeader: TemplateRef<unknown>;

  constructor(private bookingWidgetGtmService: BookingWidgetGtmService) {}

  ngOnInit(): void {
    this.pricePerAdult$ = combineLatest([this.prices$, this.travelDates$, this.isOneway$]).pipe(
      map(([prices, travelDates, isOneWay]) => getStartingFromPrice(prices, travelDates, isOneWay))
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  openModal(): void {
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    // reset histogram selected month when modal closes
    this.selectedHistogramMonth$.next(undefined);
  }

  updateCalendarDates(calendarDates: [string] | [string, string]): void {
    const [departureDate, returnDate] = calendarDates;

    // Currently the calendar emits (selectedDatesChange) events even when hovering on dates (on every render), so we'll limit
    // unnecessary emissions here by checking that the values have changed
    this.subscription.add(
      this.travelDates$
        .pipe(
          take(1),
          filter(
            (travelDates) =>
              departureDate !== travelDates.departureDate?.id || returnDate !== travelDates.returnDate?.id
          )
        )
        .subscribe(() => {
          this.setTravelDates.emit({
            departureDate: departureDate ? new LocalDate(departureDate) : undefined,
            returnDate: returnDate ? new LocalDate(returnDate) : undefined,
          });
        })
    );
  }

  setHistogramMonth(selectedMonthIndex = 0): void {
    this.selectedHistogramMonth$.next(selectedMonthIndex);
  }

  handleHistogramClick(selectedBar: HistogramBar): void {
    if (!selectedBar.noFlight) {
      this.bookingWidgetGtmService.trackElementEvent(
        'histogram',
        selectedBar?.isCheapest ? 'lowest-month-click' : 'other-month-click'
      );
    }

    this.selectedHistogramMonth$.next(selectedBar.index);
  }

  handleBarsScrolled(_direction: Direction): void {
    // TODO: for future, maybe handle the direction where the histogram is scrolled
    this.bookingWidgetGtmService.trackElementEvent('histogram', 'arrow-click');
  }
}
